import {connect} from 'react-redux';

import {ChannelModel} from '../../../../../../libs/shared/core/src/models/api/channelModel';
import {MessageModel} from '../../../../../../libs/shared/core/src/models/api/messageModel';
import {reportToBugsnag} from '../../../../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {getTimeFormat} from '../../../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {
  getHasIsCreatingFromOrphanEvent,
  getHasIsCreatingFromOrphanEventOffice365,
} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ResolveStateProps} from '../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AppParametricSelector,
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../srcCommon/helpers/reselectHelpers';
import {buildChannelSettingsPath} from '../../../../../helpers/models/channelHelpers';
import {CalendarEventVersionResponse} from '../../../../../models/api/calendarEventVersionModel';
import {downloadAttachmentAsync} from '../../../../../services/browser';
import {AppState} from '../../../../../store/appState';
import {replyToCalendarEventVersionAction} from '../../../../../store/data/calendarEventVersions/calendarEventVersionsActions';
import {makeGetReplyChannels} from '../../../../../store/data/channels/channelsSelectors';
import {makeGetVisualParticipants} from '../../../../../store/data/contacts/participantsSelectors';
import {isCreatingFromOrphanEventOpenSidePanelAction} from '../../../../../store/panels/calendars/calendarsPanelActions';
import {
  makeGetCalendarEvent,
  makeGetCalendarEventVersion,
  makeGetCalendarEventVersionsForEvent,
  makeGetMessageICSAttachments,
  makeGetReAuthChannel,
  makeGetResponseData,
} from '../../../../../store/panels/viewer/viewerCalendarInviteSelectors';
import {navigateToLinkAction} from '../../../../../store/stateless/navigationActions';
import {CalendarInviteViewer} from './calendarInviteViewer';

/*
 * Props.
 */

interface CalendarInviteViewerContainerProps {
  namespace: string;
  message: MessageModel;
  calendarEventUid: string;
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
}

/*
 * Selectors.
 */

function getNamespace(_state: AppState, props: CalendarInviteViewerContainerProps) {
  return props.namespace;
}

function getMessage(_state: AppState, props: CalendarInviteViewerContainerProps) {
  return props.message;
}

function getCalendarEventUid(_state: AppState, props: CalendarInviteViewerContainerProps) {
  return props.calendarEventUid;
}

const makeGetMessageChannelProvider = <P>(
  getReplyChannels: AppParametricSelector<P, ReadonlyArray<ChannelModel>>,
) => createSelector([getReplyChannels], (replyChannels) => replyChannels[0]?.type);

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CalendarInviteViewerContainerProps>()(() => {
  const getCalendarEvent = makeGetCalendarEvent(getCalendarEventUid);
  const getCalendarEventVersionsForEvent = makeGetCalendarEventVersionsForEvent(getCalendarEvent);
  const getReplyChannels = makeGetReplyChannels({getMessage});
  const getVisualParticipants = makeGetVisualParticipants(getNamespace, getCalendarEvent);
  const getResponseData = makeGetResponseData(
    getVisualParticipants,
    getCalendarEventVersionsForEvent,
    getReplyChannels,
  );
  const getCalendarEventVersion = makeGetCalendarEventVersion(
    getCalendarEventVersionsForEvent,
    getResponseData,
  );
  const getReAuthChannel = makeGetReAuthChannel(getCalendarEventVersion, getReplyChannels);

  const getMessageChannelProvider = makeGetMessageChannelProvider(getReplyChannels);

  return {
    calendarEvent: getCalendarEvent,
    calendarEventVersion: getCalendarEventVersion,
    visualParticipants: getVisualParticipants,
    responseData: getResponseData,
    messageICSAttachments: makeGetMessageICSAttachments(getMessage),
    reAuthChannel: getReAuthChannel,
    timeFormat: getTimeFormat,
    hasIsCreatingFromOrphanEvent: getHasIsCreatingFromOrphanEvent,
    hasIsCreatingFromOrphanEventOffice365: getHasIsCreatingFromOrphanEventOffice365,
    messageChannelProvider: getMessageChannelProvider,
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  replyToCalendarEventVersionAction,
  navigateToLinkAction,
  isCreatingFromOrphanEventOpenSidePanelAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: CalendarInviteViewerContainerProps,
) {
  const {calendarEvent, calendarEventVersion, responseData, messageICSAttachments} = stateProps;
  const {reAuthChannel} = stateProps;

  // NOTE: since isRecurringSaveTypeRequired is false for CalendarInviteViewer, we don't need to
  // handle shouldShowRecurringPrompt/masterCalendarEventVersion here.
  const updateCalendarEventResponse: (response: CalendarEventVersionResponse) => void = (newResponse) => {
    if (
      !responseData ||
      !responseData.response ||
      (newResponse.status === responseData.response.status &&
        newResponse.comment === responseData.response.comment)
    ) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.RESPOND_CALENDAR_INVITE, {
      channel_type: responseData.replyChannel && responseData.replyChannel.type,
      status: newResponse.status,
      has_comment: Boolean(newResponse.comment),
    });

    dispatchProps.replyToCalendarEventVersionAction({
      calendarEventVersionUid: calendarEventVersion.uid,
      response: newResponse,
      channelId: responseData.replyChannel.id,
      calendarVersionId: responseData.replyCalendarVersion && responseData.replyCalendarVersion.id,
    });
  };

  const downloadICSAttachments = async () => {
    try {
      const downloadAttachmentPromises = messageICSAttachments.map((a) => downloadAttachmentAsync(a));
      await Promise.all(downloadAttachmentPromises);
    } catch (error) {
      reportToBugsnag(error);
    }
  };

  const onGoToReAuthChannelSettingsClick = () => {
    if (!reAuthChannel) {
      return;
    }

    const settingsPath = buildChannelSettingsPath(reAuthChannel);
    dispatchProps.navigateToLinkAction(settingsPath);
  };

  const onAddToCalendar = () => {
    dispatchProps.isCreatingFromOrphanEventOpenSidePanelAction(
      calendarEvent,
      calendarEventVersion,
      ownProps.panelId,
    );
  };

  return {
    ...stateProps,
    ...ownProps,
    hasICSAttachments: messageICSAttachments.length > 0,
    replyChannel: responseData && responseData.replyChannel,
    calendarVersion: responseData && responseData.replyCalendarVersion,
    response: responseData && responseData.response,
    updateCalendarEventResponse,
    downloadICSAttachments,
    onGoToReAuthChannelSettingsClick,
    onAddToCalendar,
  };
}

export const CalendarInviteViewerContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(CalendarInviteViewer);
